import { z } from 'zod';

export type SnapshotStatsFilter = Partial<
    Record<keyof SnapshotStats, Partial<Record<keyof SnapshotStatsEntry, number | [number | null, number | null]>>>
>;

/**
 * Per-camera cart-image tallies on a session. Mirrors .NET `CameraTriggerStatsModel`, whose `[JsonProperty]`
 * names these keys must match.
 *
 * Both counters are observed in the cloud, over the stored image metadata. The cart-observed `triggered`,
 * `captured` and `failed` were removed with `cart_snapshotService` (#42118): it was their only writer, so they
 * would report zero for every session from that deploy on. This schema stops describing them, which also narrows
 * what `SnapshotStatsFilter` lets the DSI session filter query.
 *
 * An object rather than `z.record(z.enum(...))` on purpose, and every counter optional: a record keyed by an enum
 * is *exhaustive and strict* in zod 4, so it rejects a document that carries a key it does not list as much as one
 * that omits a key it does. Both happen here — every session written before #42118 still carries the three
 * cart-observed counters, and nothing guarantees a session has both of the remaining ones — and rejecting either
 * fails the whole `CustomerSessionTracking` parse rather than the one field. Stripping the retired keys is the
 * point: they are readable in Mongo for anyone who needs the history, and absent from what this platform serves.
 */
export const SnapshotStatsEntry = z.object({
    transferred: z.number().optional(),
    withClassifications: z.number().optional(),
});
export type SnapshotStatsEntry = z.infer<typeof SnapshotStatsEntry>;

/** Per-camera entries. Optional for the same reason as the counters — a session need not have every camera. */
export const SnapshotStats = z.object({
    bottom: SnapshotStatsEntry.nullish(),
    top: SnapshotStatsEntry.nullish(),
    total: SnapshotStatsEntry.nullish(),
});

export type SnapshotStats = z.infer<typeof SnapshotStats>;
